import ErmLandingPage from '@shared/components/navigation/landing-page/landing-page.erm';
import { html, nothing } from '@shared/utils/rendering/html';
import {
    GenerateHomeDefaultConfig,
    GenerateHomeOptions,
} from '../../configs/navigation/home.config';
import { useLocation } from 'preact-iso';
import { GenerateUserAccountConfig } from '../../configs/navigation/user.config';
import { ERMPortalUrls } from '../../constants/routing/urls.constants';
import { useAuth } from '../../utils/context/auth-context';
import { useMemo, useCallback } from 'preact/hooks';

import { useFeatureChecks } from '../../utils/features/feature-context';
import { useSystemStatusPolling } from '../../utils/hooks/use-system-status-polling';
import { SystemKeys } from '../../constants/security/systems.constants';
import { isProduction } from '../../constants/utility/env.constants';

const LandingPage = () => {
    const { route } = useLocation();
    const { logout, user } = useAuth();
    const { map: featureMap } = useFeatureChecks();

    const systemCheck = useCallback(
        (system, fallback) => {
            const key = SystemKeys[system];
            if (!key) return fallback;

            return Object.prototype.hasOwnProperty.call(featureMap, key)
                ? Boolean(featureMap[key])
                : fallback;
        },
        [featureMap]
    );

    const systemStatuses = useSystemStatusPolling();

    const defaultOptions = useMemo(() => {
        if (logout) {
            const config = GenerateHomeDefaultConfig(GenerateUserAccountConfig(logout, user), user);
            return config;
        }
    }, [logout, user]);

    // systemStatuses must be a dependency: the status poll resolves after the first render, and
    // without it the tiles stay built from the initial empty statuses - every app looks online.
    const mainOptions = useMemo(() => {
        return GenerateHomeOptions(user, systemCheck, systemStatuses).map((button) => {
            const href = button?.props?.href;
            const shouldUseSpaRouting = typeof href === 'string' && href.startsWith('/');

            return {
                ...button,
                props: {
                    ...(button ? button.props : {}),
                    ...(shouldUseSpaRouting ? { onClick: route } : {}),
                },
            };
        });
    }, [route, systemCheck, user, systemStatuses]);

    const resolvedMainOptions = useMemo(() => {
        if (mainOptions.length > 0) {
            return mainOptions;
        }

        return [
            {
                label: { text: 'My Access' },
                props: {
                    href: ERMPortalUrls.myAccess,
                    target: '_blank',
                },
            },
        ];
    }, [mainOptions]);

    const environment = isProduction() ? 'production' : 'non-production';

    return defaultOptions?.length
        ? html` <${ErmLandingPage}
              options=${{
                  default: defaultOptions,
                  main: resolvedMainOptions,
              }}
              config=${{
                  brand: {
                      text: 'Enterprise Risk Management',
                  },
                  theme: {
                      background: 'default',
                      environment,
                      brandSpace: 'default',
                      brandTitle: 'default',
                  },
              }}
          />`
        : nothing;
};

export default LandingPage;
